import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase, supabaseReady } from "@/lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileChecked, setProfileChecked] = useState(false);

  const loadProfile = useCallback(async (userId) => {
    if (!userId || !supabase) {
      setProfile(null);
      setProfileChecked(true);
      return null;
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("id,email,full_name,is_admin")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.error("[AuthContext] loadProfile error:", error);
      setProfile(null);
      setProfileChecked(true);
      return null;
    }
    setProfile(data ?? null);
    setProfileChecked(true);
    return data ?? null;
  }, []);

  useEffect(() => {
    if (!supabaseReady) {
      setLoading(false);
      return;
    }
    let mounted = true;

    // Callback must stay synchronous: awaiting Supabase queries inside it deadlocks
    // the auth lock and stalls every later query until a page refresh.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession ?? null);
      if (event === "TOKEN_REFRESHED") return;
      const uid = newSession?.user?.id;
      if (!uid) {
        setProfile(null);
        setProfileChecked(true);
        setLoading(false);
        return;
      }
      setProfileChecked(false);
      setTimeout(async () => {
        try { await loadProfile(uid); }
        finally { if (mounted) setLoading(false); }
      }, 0);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [loadProfile]);

  const signIn = async (email, password) => {
    if (!supabase) throw new Error("Auth is not configured yet.");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setProfileChecked(true);
  };

  const user = session?.user
    ? {
        id: session.user.id,
        email: session.user.email,
        name: profile?.full_name || session.user.email,
        role: profile?.is_admin ? "admin" : "user",
        is_admin: !!profile?.is_admin,
      }
    : null;

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, profileChecked, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

